import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useReducedMotionSafe } from "@/components/animation/AnimationSettings";
import { PALETTE, scoreColor } from "@/components/animation/MotionShared";

/**
 * Animated ORACLE Score Ring (0–100).
 * Premium gold-rimmed circular gauge with motion fill and centered numeric.
 * Respects reduced motion. Pure SVG — no extra deps.
 */
export default function OracleScoreRing({
  score = 0,
  size = 120,
  label = "ORACLE SCORE™",
  testid = "oracle-score-ring",
  compact = false,
}) {
  const reduce = useReducedMotionSafe();
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const sizeN = (() => {
    const n = Number(size);
    return Number.isFinite(n) && n > 0 ? n : 120;
  })();
  const stroke = compact ? 8 : 10;
  const radius = Math.max(1, (sizeN - stroke * 1.6) / 2);
  const circumference = 2 * Math.PI * radius;
  const fillColor = scoreColor(s);
  const dash = useMemo(() => circumference * (s / 100), [circumference, s]);
  const center = sizeN / 2;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }} data-testid={testid}>
      <svg width={sizeN} height={sizeN} viewBox={`0 0 ${sizeN} ${sizeN}`} role="img" aria-label={`${label} ${s}/100`}>
        <defs>
          <linearGradient id={`osr-${testid}-grad`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"  stopColor={fillColor} stopOpacity="1" />
            <stop offset="100%" stopColor={PALETTE.gold} stopOpacity="0.85" />
          </linearGradient>
          <radialGradient id={`osr-${testid}-glow`} cx="50%" cy="50%" r="55%">
            <stop offset="0%"  stopColor={fillColor} stopOpacity="0.18" />
            <stop offset="100%" stopColor={fillColor} stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx={center} cy={center} r={radius + stroke * 0.6} fill={`url(#osr-${testid}-glow)`} />
        <circle cx={center} cy={center} r={radius}
          stroke={"rgba(255,255,255,0.08)"} strokeWidth={stroke} fill="none"
        />
        <motion.circle
          cx={center} cy={center} r={radius}
          stroke={`url(#osr-${testid}-grad)`}
          strokeLinecap="round"
          strokeWidth={stroke}
          fill="none"
          transform={`rotate(-90 ${center} ${center})`}
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference - dash }}
          transition={reduce ? { duration: 0 } : { duration: 1.2, ease: "easeOut" }}
        />
        <text x="50%" y="49%" textAnchor="middle" dominantBaseline="middle"
          style={{ fill: PALETTE.goldBright, fontFamily: "'Space Mono', monospace", fontWeight: 800, fontSize: compact ? 22 : 26 }}
        >
          {Math.round(s)}
        </text>
        <text x="50%" y="66%" textAnchor="middle" dominantBaseline="middle"
          style={{ fill: PALETTE.slate4, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.18em" }}
        >
          /100
        </text>
      </svg>
      {!compact && (
        <span style={{ color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.16em" }}>
          {label}
        </span>
      )}
    </div>
  );
}
